import { useState, useEffect, useRef, useMemo } from "react";
import { CardListSkeleton } from "@/components/SkeletonLoaders";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";
import { useLocation } from "@/lib/locationContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  BarChart3, Upload, Plus, X, ChevronDown, ChevronUp,
  DollarSign, TrendingUp, TrendingDown,
} from "lucide-react";
import { toast } from "sonner";
import { format, parse } from "date-fns";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";

const MIAMI_AVG = {
  foodCostPct: 30,
  laborCostPct: 32,
  rentPct: 8,
  profitMarginPct: 6,
};

interface CheckIn {
  id: string;
  month_label: string;
  revenue: number;
  labor_cost: number;
  food_cost: number;
  rent: number;
  created_at: string;
}

type Metrics = {
  foodPct: number;
  laborPct: number;
  rentPct: number;
  profitMargin: number;
};

function getMetrics(c: CheckIn): Metrics {
  const rev = c.revenue || 1;
  return {
    foodPct: (c.food_cost / rev) * 100,
    laborPct: (c.labor_cost / rev) * 100,
    rentPct: (c.rent / rev) * 100,
    profitMargin: ((rev - c.food_cost - c.labor_cost - c.rent) / rev) * 100,
  };
}

function formatMonthLabel(label: string): string {
  try {
    const d = parse(label, "yyyy-MM", new Date());
    return format(d, "MMM yyyy");
  } catch {
    return label;
  }
}

function MetricDot({ value, avg, inverse = false }: { value: number; avg: number; inverse?: boolean }) {
  const good = inverse ? value <= avg : value >= avg;
  return (
    <span className={`inline-block w-2 h-2 rounded-full ${good ? "bg-primary" : "bg-destructive"}`} />
  );
}

// --- Month Card ---
function MonthCard({ checkin, expanded, onToggle }: {
  checkin: CheckIn; expanded: boolean; onToggle: () => void;
}) {
  const m = getMetrics(checkin);
  const rows: { label: string; value: number; avg: number; inverse: boolean }[] = [
    { label: "Food", value: m.foodPct, avg: MIAMI_AVG.foodCostPct, inverse: true },
    { label: "Labor", value: m.laborPct, avg: MIAMI_AVG.laborCostPct, inverse: true },
    { label: "Rent", value: m.rentPct, avg: MIAMI_AVG.rentPct, inverse: true },
    { label: "Profit", value: m.profitMargin, avg: MIAMI_AVG.profitMarginPct, inverse: false },
  ];

  return (
    <motion.div layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
      <Card
        className={`cursor-pointer transition-shadow hover:shadow-md ${expanded ? "ring-1 ring-primary/30" : ""}`}
        onClick={onToggle}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">
              {formatMonthLabel(checkin.month_label)}
            </span>
            {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </div>

          {/* Compact metric dots */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {rows.map(r => (
              <div key={r.label} className="flex items-center gap-1.5">
                <MetricDot value={r.value} avg={r.avg} inverse={r.inverse} />
                <span className="text-xs text-muted-foreground">{r.label}</span>
                <span className="text-xs font-medium text-foreground ml-auto">{r.value.toFixed(1)}%</span>
              </div>
            ))}
          </div>

          {/* Expanded breakdown */}
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-3 pt-3 border-t border-border space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Revenue</span>
                    <span className="font-medium text-foreground">${checkin.revenue.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Food Cost</span>
                    <span className="font-medium text-foreground">${checkin.food_cost.toLocaleString()}
                      <span className={`ml-1 text-xs ${m.foodPct > MIAMI_AVG.foodCostPct + 2 ? "text-destructive" : "text-primary"}`}>
                        ({m.foodPct.toFixed(1)}% vs {MIAMI_AVG.foodCostPct}%)
                      </span>
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Labor Cost</span>
                    <span className="font-medium text-foreground">${checkin.labor_cost.toLocaleString()}
                      <span className={`ml-1 text-xs ${m.laborPct > MIAMI_AVG.laborCostPct + 2 ? "text-destructive" : "text-primary"}`}>
                        ({m.laborPct.toFixed(1)}% vs {MIAMI_AVG.laborCostPct}%)
                      </span>
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Rent</span>
                    <span className="font-medium text-foreground">${checkin.rent.toLocaleString()}
                      <span className={`ml-1 text-xs ${m.rentPct > MIAMI_AVG.rentPct + 2 ? "text-destructive" : "text-primary"}`}>
                        ({m.rentPct.toFixed(1)}% vs {MIAMI_AVG.rentPct}%)
                      </span>
                    </span>
                  </div>
                  <div className="flex justify-between text-sm pt-1 border-t border-border">
                    <span className="text-muted-foreground font-medium">Profit Margin</span>
                    <span className={`font-medium ${m.profitMargin >= MIAMI_AVG.profitMarginPct ? "text-primary" : "text-destructive"}`}>
                      {m.profitMargin.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// --- CSV Parsing Utilities ---
const REVENUE_KEYS = [
  "net sales", "gross sales", "total sales", "revenue", "net revenue",
  "total revenue", "total net sales", "sales", "amount", "total amount",
  "net total", "gross revenue",
];
const FOOD_COST_KEYS = [
  "food cost", "cogs", "cost of goods", "cost of goods sold",
  "food & beverage cost", "food and beverage cost", "food expenses",
  "inventory cost", "product cost",
];
const LABOR_COST_KEYS = [
  "labor cost", "labor", "payroll", "total labor", "labor expenses",
  "wages", "total payroll", "staff cost", "employee cost",
  "total labor cost",
];

function parseCsvValue(val: string): number {
  if (!val) return 0;
  const cleaned = val.replace(/[$,"\s]/g, "").replace(/[()]/g, "");
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : Math.round(Math.abs(num));
}

function findColumn(headers: string[], keys: string[]): number {
  const normalized = headers.map(h => h.toLowerCase().trim());
  for (const key of keys) {
    const idx = normalized.indexOf(key);
    if (idx !== -1) return idx;
  }
  for (const key of keys) {
    const idx = normalized.findIndex(h => h.includes(key));
    if (idx !== -1) return idx;
  }
  return -1;
}

function parseCSV(text: string): { revenue: number; foodCost: number; laborCost: number } | null {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return null;
  let headerIdx = -1;
  let headers: string[] = [];
  for (let i = 0; i < Math.min(lines.length, 5); i++) {
    const cols = lines[i].split(",");
    if (findColumn(cols, REVENUE_KEYS) !== -1) { headerIdx = i; headers = cols; break; }
  }
  if (headerIdx === -1) return null;
  const revCol = findColumn(headers, REVENUE_KEYS);
  const foodCol = findColumn(headers, FOOD_COST_KEYS);
  const laborCol = findColumn(headers, LABOR_COST_KEYS);
  if (revCol === -1) return null;
  let revenue = 0, foodCost = 0, laborCost = 0;
  for (let i = headerIdx + 1; i < lines.length; i++) {
    const cols = lines[i].split(",");
    if (cols.length <= revCol) continue;
    revenue += parseCsvValue(cols[revCol]);
    if (foodCol !== -1 && cols.length > foodCol) foodCost += parseCsvValue(cols[foodCol]);
    if (laborCol !== -1 && cols.length > laborCol) laborCost += parseCsvValue(cols[laborCol]);
  }
  return { revenue, foodCost, laborCost };
}

// --- Main Component ---
export default function MonthlyCheckIn() {
  const { user } = useAuth();
  const { activeLocation } = useLocation();
  const locId = activeLocation?.id || null;
  const [checkins, setCheckins] = useState<CheckIn[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form state
  const [monthLabel, setMonthLabel] = useState(format(new Date(), "yyyy-MM"));
  const [revenue, setRevenue] = useState("");
  const [laborCost, setLaborCost] = useState("");
  const [foodCost, setFoodCost] = useState("");
  const [rent, setRent] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchCheckins = async () => {
    if (!user) return;
    let q = supabase.from("monthly_checkins").select("*").eq("user_id", user.id);
    if (locId) q = q.eq("location_id", locId);
    const { data } = await q.order("month_label", { ascending: true });
    setCheckins((data as CheckIn[]) || []);
    setLoading(false);
  };

  useEffect(() => { fetchCheckins(); }, [user, locId]);

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".csv")) { toast.error("Please upload a CSV file"); return; }
    const reader = new FileReader();
    reader.onload = (evt) => {
      const result = parseCSV(evt.target?.result as string);
      if (!result) { toast.error("Could not find revenue columns in CSV"); return; }
      setRevenue(result.revenue.toString());
      if (result.foodCost > 0) setFoodCost(result.foodCost.toString());
      if (result.laborCost > 0) setLaborCost(result.laborCost.toString());
      const found = [result.revenue > 0 && "Revenue", result.foodCost > 0 && "Food Cost", result.laborCost > 0 && "Labor Cost"].filter(Boolean);
      toast.success(`Imported ${found.join(", ")} from CSV`);
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!user) return;
    const rev = parseInt(revenue) || 0;
    const lab = parseInt(laborCost) || 0;
    const food = parseInt(foodCost) || 0;
    const r = parseInt(rent) || 0;
    if (!rev) { toast.error("Revenue is required"); return; }

    // Check for duplicate month
    const exists = checkins.find(c => c.month_label === monthLabel);
    if (exists) { toast.error(`You already have data for ${formatMonthLabel(monthLabel)}`); return; }

    const { error } = await supabase.from("monthly_checkins").insert({
      user_id: user.id, month_label: monthLabel, revenue: rev, labor_cost: lab, food_cost: food, rent: r,
      ...(locId ? { location_id: locId } : {}),
    });
    if (error) { toast.error("Failed to save check-in"); return; }
    toast.success(`${formatMonthLabel(monthLabel)} check-in saved!`);
    setShowForm(false);
    setRevenue(""); setLaborCost(""); setFoodCost(""); setRent("");
    setMonthLabel(format(new Date(), "yyyy-MM"));
    fetchCheckins();
  };

  // Chart data
  const chartData = useMemo(() => {
    return checkins.map(c => {
      const m = getMetrics(c);
      return {
        month: formatMonthLabel(c.month_label),
        "Food Cost %": parseFloat(m.foodPct.toFixed(1)),
        "Labor Cost %": parseFloat(m.laborPct.toFixed(1)),
        "Profit Margin %": parseFloat(m.profitMargin.toFixed(1)),
      };
    });
  }, [checkins]);

  // Sort checkins newest first for display
  const sortedCheckins = useMemo(() => [...checkins].reverse(), [checkins]);

  if (loading) {
    return <CardListSkeleton rows={3} />;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" /> Monthly Check-In
          </CardTitle>
          <Button size="sm" onClick={() => setShowForm(!showForm)} variant={showForm ? "outline" : "default"} className="gap-1.5">
            {showForm ? <><X className="w-4 h-4" /> Cancel</> : <><Plus className="w-4 h-4" /> Add Month</>}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Add Month Form */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="space-y-4 p-4 rounded-lg border border-border bg-muted/20">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">New Monthly Entry</Label>
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleCsvUpload}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="gap-1.5 h-7 text-xs"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      Import CSV
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Month</Label>
                    <Input type="month" value={monthLabel} onChange={e => setMonthLabel(e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Revenue ($)</Label>
                    <Input type="number" value={revenue} onChange={e => setRevenue(e.target.value)} placeholder="45000" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Food Cost ($)</Label>
                    <Input type="number" value={foodCost} onChange={e => setFoodCost(e.target.value)} placeholder="13500" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Labor Cost ($)</Label>
                    <Input type="number" value={laborCost} onChange={e => setLaborCost(e.target.value)} placeholder="14000" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Rent ($)</Label>
                    <Input type="number" value={rent} onChange={e => setRent(e.target.value)} placeholder="5000" />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleSubmit} disabled={!revenue} className="w-full">Save</Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Works with Toast, Square, Clover, and most POS CSV exports.</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Month History Grid */}
        {sortedCheckins.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {sortedCheckins.map(c => (
                <MonthCard
                  key={c.id}
                  checkin={c}
                  expanded={expandedId === c.id}
                  onToggle={() => setExpandedId(prev => prev === c.id ? null : c.id)}
                />
              ))}
            </div>

            {/* Trend Chart */}
            {chartData.length >= 2 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
                <Card className="border-border/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-primary" /> Performance Trends
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pb-4">
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                        <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" unit="%" />
                        <Tooltip
                          contentStyle={{
                            background: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: 8,
                            fontSize: 12,
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="Food Cost %" stroke="hsl(var(--destructive))" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="Labor Cost %" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="Profit Margin %" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      Miami averages: Food {MIAMI_AVG.foodCostPct}% · Labor {MIAMI_AVG.laborCostPct}% · Profit {MIAMI_AVG.profitMarginPct}%
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <DollarSign className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No monthly data yet. Click <strong>+ Add Month</strong> to start tracking.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}